"use client";

import { useMemo, useState, type ReactNode } from "react";
import {
  CHARGER_OPTIONS,
  DEFAULT_INPUTS,
  DNO_PEAK,
  SLOTS,
  SLOT_HOURS,
  evaluate,
  peakOf,
  slotLabel,
  type CapacityInputs,
  type CapacityResult,
} from "@/lib/capacity-check/model";
import CapacityCheckRequest from "./CapacityCheckRequest";

/**
 * The public Depot Capacity Check (/capacity-check). Inputs on the left like a
 * connection application form, the answer and the depot's night on the right.
 *
 * Every figure comes from lib/capacity-check/model.ts, computed in the
 * visitor's browser. Nothing is sent anywhere until the visitor chooses to:
 * the request form (CapacityCheckRequest.tsx) sends their details and results
 * to AmpVerve's CRM when they press Send, and the email link opens their own
 * mail client, which is also the fallback when the form cannot send.
 */

const CONTACT_EMAIL = "hello@ampverve.com";
const DISCLAIMER = "A planning estimate from the figures you enter, not a DNO connection assessment.";

const fmt = (x: number, d = 0) =>
  x.toLocaleString("en-GB", { maximumFractionDigits: d, minimumFractionDigits: d });
const gbp = (x: number) => `£${fmt(Math.round(x))}`;
const OVER = 1e-6;

/** Noon to noon, so the night reads as one stretch. */
const ORDER = Array.from({ length: SLOTS }, (_, i) => (i + 24) % SLOTS);

type Tone = "bad" | "ok" | "";
interface Fact {
  k: string;
  v: string;
  tone: Tone;
}
interface Headline {
  warn: string;
  rest: string;
}

function headlineOf(r: CapacityResult): Headline {
  const p = r.params;
  const n = p.n;
  const dwell = fmt(p.win.length * SLOT_HOURS, 1);
  if (r.tooShort) {
    return {
      warn: `A van cannot take ${fmt(p.energy, 1)} kWh in ${dwell} hours at ${p.kw} kW.`,
      rest: "Choose a faster charger or a longer stay.",
    };
  }
  if (!r.smart.short) {
    const over = r.onArrivalPeak > p.cap + OVER;
    return {
      warn: "",
      rest: over
        ? `Your ${n} vans fit on ${fmt(p.cap)} kW with smart charging. Charged on arrival they would need ${fmt(r.onArrivalPeak)} kW, ${fmt(r.onArrivalPeak - p.cap)} kW over the connection.`
        : `Your ${n} vans fit on ${fmt(p.cap)} kW with smart charging, and on arrival charging too.`,
    };
  }
  return {
    warn: `${n} vans do not fit, even smart charged.`,
    rest: `${fmt(r.maxSmart)} do. The gap is ${n - r.maxSmart} vans: a battery, a timed connection or an upgrade would close it.`,
  };
}

function factsOf(r: CapacityResult): Fact[] {
  const p = r.params;
  const facts: Fact[] = [
    { k: "Peak, charged on arrival", v: `${fmt(r.onArrivalPeak)} kW`, tone: r.onArrivalPeak > p.cap + OVER ? "bad" : "" },
    { k: "Peak, smart charging", v: `${fmt(r.smartPeak)} kW`, tone: r.smart.short ? "bad" : "ok" },
  ];
  const against =
    r.savingBaseline === "on_arrival" ? "against charging on arrival" : "against charging on arrival held to the connection";
  if (r.annualCostDifference !== null) {
    const diff = r.annualCostDifference;
    facts.push({
      k: `Estimated charging cost saved a year (${p.days} days), ${against}`,
      v: diff > 0.5 ? gbp(diff) : "£0",
      tone: diff > 0.5 ? "ok" : "",
    });
  } else if (!r.tooShort && r.savingNotStatedReason) {
    facts.push({ k: `Estimated charging cost saved a year. ${r.savingNotStatedReason}`, v: "Not shown", tone: "" });
  }
  if (r.timed) {
    facts.push({ k: "Import needed 16:00 to 19:00", v: `${fmt(r.timed.peak)} kW`, tone: r.timed.peak < p.cap ? "ok" : "" });
  }
  return facts;
}

function summaryOf(inputs: CapacityInputs, r: CapacityResult, head: Headline, facts: Fact[]): string {
  const p = r.params;
  return [
    "Depot Capacity Check (AmpVerve DepotTwin)",
    `Connection: ${inputs.kva} kVA at power factor ${inputs.powerFactor} (${fmt(p.cap)} kW). Site load ${inputs.dayLoadKw} kW day, ${inputs.nightLoadKw} kW night.`,
    `Fleet: ${p.n} vans, ${inputs.arrive} to ${inputs.depart}, ${inputs.milesPerDay} mi/day at ${inputs.milesPerKwh} mi/kWh, ${p.kw} kW chargers.`,
    `Tariff: ${inputs.dayRatePence}p/kWh day, ${inputs.nightRatePence}p/kWh night (00:00 to 07:00), ${inputs.operatingDays} operating days.`,
    `Vans that fit: ${fmt(r.maxOnArrival)} charged on arrival, ${fmt(r.maxSmart)} with smart charging.`,
    [head.warn, head.rest].filter(Boolean).join(" "),
    facts.map((f) => `${f.k}: ${f.v}`).join("; "),
    DISCLAIMER,
  ].join("\n");
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the textarea route
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/* ─── Form pieces ─────────────────────────────────────────────────── */

const fieldBox =
  "flex items-center rounded-lg border border-border bg-background focus-within:ring-2 focus-within:ring-accent/60";
const fieldInput =
  "w-full min-w-0 border-0 bg-transparent px-2.5 py-2 font-mono text-[15px] font-medium text-foreground tabular-nums outline-none";

function Field({
  id,
  label,
  hint,
  unit,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  unit?: string;
  children: ReactNode;
}) {
  return (
    <label htmlFor={id} className="grid gap-1 text-[13px] font-medium text-foreground">
      <span>
        {label}
        {hint ? <span className="ml-1 text-[12px] font-normal text-muted">{hint}</span> : null}
      </span>
      <span className={fieldBox}>
        {children}
        {unit ? <span className="whitespace-nowrap pr-2.5 font-mono text-[12px] text-muted">{unit}</span> : null}
      </span>
    </label>
  );
}

function Legend({ className, label, dashed, thick }: { className: string; label: string; dashed?: boolean; thick?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <svg width="18" height="8" aria-hidden="true">
        <line
          x1="0"
          x2="18"
          y1="4"
          y2="4"
          className={className}
          strokeWidth={thick ? 8 : 3}
          strokeDasharray={dashed ? "4 3" : undefined}
        />
      </svg>
      {label}
    </span>
  );
}

/* ─── Chart ───────────────────────────────────────────────────────── */

const W = 640;
const H = 250;
const PAD_L = 46;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 30;

function NightChart({ r, cursor }: { r: CapacityResult; cursor: number }) {
  const p = r.params;
  const ymax = Math.max(1, Math.max(p.cap, peakOf(r.onArrivalSite), peakOf(r.smartSite)) * 1.12);
  const step = [5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000].find((s) => ymax / s <= 6) ?? 10000;
  const X = (i: number) => PAD_L + (i * (W - PAD_L - PAD_R)) / SLOTS;
  const Y = (v: number) => PAD_T + (1 - v / ymax) * (H - PAD_T - PAD_B);
  const stepPath = (arr: number[]) => ORDER.map((s, i) => `${i ? "L" : "M"}${X(i)},${Y(arr[s])}H${X(i + 1)}`).join("");
  const baseArea =
    `M${X(0)},${Y(0)}` + ORDER.map((s, i) => `L${X(i)},${Y(p.base[s])}H${X(i + 1)}`).join("") + `L${X(SLOTS)},${Y(0)}Z`;
  const grid: number[] = [];
  for (let v = 0; v <= ymax; v += step) grid.push(v);
  const ticks: number[] = [];
  for (let i = 0; i <= SLOTS; i += 8) ticks.push(i);
  const peakX0 = X(DNO_PEAK[0] - 24);
  const peakX1 = X(DNO_PEAK[1] - 24);
  const cursorX = PAD_L + ((cursor + 0.5) * (W - PAD_L - PAD_R)) / SLOTS;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Site import across the night, noon to noon"
      className="h-auto w-full font-mono text-[11px]"
    >
      {grid.map((v) => (
        <g key={`g${v}`}>
          <line x1={PAD_L} x2={W - PAD_R} y1={Y(v)} y2={Y(v)} className="stroke-border" />
          <text x={PAD_L - 6} y={Y(v) + 4} textAnchor="end" className="fill-muted">
            {fmt(v)}
          </text>
        </g>
      ))}
      {ticks.map((i) => (
        <text key={`t${i}`} x={X(i)} y={H - 10} textAnchor="middle" className="fill-muted">
          {slotLabel(ORDER[i % SLOTS])}
        </text>
      ))}
      <rect x={peakX0} y={PAD_T} width={peakX1 - peakX0} height={H - PAD_T - PAD_B} className="fill-amber-400" opacity={0.08} />
      <text x={(peakX0 + peakX1) / 2} y={PAD_T + 12} textAnchor="middle" className="fill-muted">
        DNO peak
      </text>
      <path d={baseArea} className="fill-slate-600" opacity={0.7} />
      <path d={stepPath(r.smartSite)} fill="none" className="stroke-accent" strokeWidth={2.5} />
      <path d={stepPath(r.onArrivalSite)} fill="none" className="stroke-red-400" strokeWidth={1.75} />
      <line x1={PAD_L} x2={W - PAD_R} y1={Y(p.cap)} y2={Y(p.cap)} className="stroke-amber-400" strokeWidth={2} strokeDasharray="6 5" />
      <text x={W - PAD_R} y={Y(p.cap) - 6} textAnchor="end" className="fill-amber-400">
        {fmt(p.cap)} kW connection
      </text>
      <line x1={cursorX} x2={cursorX} y1={PAD_T} y2={H - PAD_B} className="stroke-foreground" strokeOpacity={0.35} />
    </svg>
  );
}

/* ─── Page ────────────────────────────────────────────────────────── */

export default function CapacityCheck() {
  const [inputs, setInputs] = useState<CapacityInputs>(DEFAULT_INPUTS);
  const [cursor, setCursor] = useState(13);
  const [copyState, setCopyState] = useState<"" | "copied" | "blocked">("");

  const r = useMemo(() => evaluate(inputs), [inputs]);
  const head = useMemo(() => headlineOf(r), [r]);
  const facts = useMemo(() => factsOf(r), [r]);
  const summary = useMemo(() => summaryOf(inputs, r, head, facts), [inputs, r, head, facts]);

  const p = r.params;
  const set = (key: keyof CapacityInputs) => (e: { target: { value: string } }) => {
    setInputs((prev) => ({ ...prev, [key]: e.target.value }));
    setCopyState("");
  };

  const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    `DepotTwin study request: ${p.n} vans on ${fmt(p.cap)} kW`,
  )}&body=${encodeURIComponent(
    `Hello AmpVerve,\n\nI would like a DepotTwin study for my depot. My Depot Capacity Check results:\n\n${summary}\n\nSite name and postcode:\nBest contact number:\n\nIf you have them, attach your charge point session export (CSV) and a recent electricity bill.\n`,
  )}`;

  const onCopy = async () => {
    setCopyState((await copyText(summary)) ? "copied" : "blocked");
  };

  // Scrub readout at the cursor slot.
  const s = ORDER[cursor];
  const inWindow = p.win.includes(s);
  const perVan = r.smart.load[s] / p.n;
  const spare = Math.max(0, p.cap - r.smartSite[s]);
  const arrivalAtCursor = r.onArrivalSite[s];
  const arrivalOver = arrivalAtCursor > p.cap + OVER;

  const toneBorder: Record<Tone, string> = { bad: "border-red-400", ok: "border-emerald-400", "": "border-border" };
  const toneText: Record<Tone, string> = { bad: "text-red-400", ok: "text-emerald-400", "": "text-foreground" };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="mx-auto max-w-[1120px] px-4 pb-14 pt-7 sm:px-5">
        <div className="flex flex-wrap gap-2.5 font-mono text-[12px] uppercase tracking-[0.08em] text-muted">
          <span className="font-medium text-accent">AmpVerve DepotTwin</span>
          <span>Free capacity check</span>
        </div>
        <h1 className="mb-2.5 mt-3 max-w-[22ch] font-display text-[clamp(30px,5vw,50px)] font-bold leading-[1.04] tracking-tight [text-wrap:balance]">
          How many electric vans can your depot charge on the connection it already has?
        </h1>
        <p className="mb-6 max-w-[62ch] text-[15px] leading-relaxed text-muted">
          Enter your connection size, your site&apos;s own load and how your vans work. The check compares charging every
          van at full power when it plugs in with smart charging that shares the connection through the night. It runs in
          your browser; nothing you type is sent anywhere unless you choose to send it to us.
        </p>

        <div className="grid items-start gap-6 min-[860px]:grid-cols-[minmax(0,330px)_minmax(0,1fr)]">
          <form
            noValidate
            onSubmit={(e) => e.preventDefault()}
            className="grid gap-4 rounded-xl border border-border bg-surface p-4 shadow-card"
          >
            <fieldset className="grid gap-2.5">
              <legend className="mb-2 font-mono text-[11px] uppercase tracking-[0.1em] text-muted">Grid connection</legend>
              <Field id="cc-kva" label="Agreed capacity (from your bill or DNO offer)" unit="kVA">
                <input id="cc-kva" type="number" min={1} step={1} inputMode="decimal" value={inputs.kva} onChange={set("kva")} className={fieldInput} />
              </Field>
              <div className="grid grid-cols-2 gap-2.5">
                <Field id="cc-day" label="Site load by day" hint="07:00 to 19:00" unit="kW">
                  <input id="cc-day" type="number" min={0} step={1} inputMode="decimal" value={inputs.dayLoadKw} onChange={set("dayLoadKw")} className={fieldInput} />
                </Field>
                <Field id="cc-night" label="Site load at night" unit="kW">
                  <input id="cc-night" type="number" min={0} step={1} inputMode="decimal" value={inputs.nightLoadKw} onChange={set("nightLoadKw")} className={fieldInput} />
                </Field>
              </div>
            </fieldset>

            <fieldset className="grid gap-2.5">
              <legend className="mb-2 font-mono text-[11px] uppercase tracking-[0.1em] text-muted">Fleet</legend>
              <Field id="cc-vans" label="Vans you want to run electric" unit="vans">
                <input id="cc-vans" type="number" min={1} step={1} inputMode="numeric" value={inputs.vans} onChange={set("vans")} className={fieldInput} />
              </Field>
              <div className="grid grid-cols-2 gap-2.5">
                <Field id="cc-arrive" label="Back at depot">
                  <input id="cc-arrive" type="time" step={1800} value={inputs.arrive} onChange={set("arrive")} className={fieldInput} />
                </Field>
                <Field id="cc-depart" label="Out next morning">
                  <input id="cc-depart" type="time" step={1800} value={inputs.depart} onChange={set("depart")} className={fieldInput} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <Field id="cc-miles" label="Miles per van per day" unit="mi">
                  <input id="cc-miles" type="number" min={1} step={1} inputMode="decimal" value={inputs.milesPerDay} onChange={set("milesPerDay")} className={fieldInput} />
                </Field>
                <Field id="cc-eff" label="Efficiency" unit="mi/kWh">
                  <input id="cc-eff" type="number" min={0.5} step={0.1} inputMode="decimal" value={inputs.milesPerKwh} onChange={set("milesPerKwh")} className={fieldInput} />
                </Field>
              </div>
              <Field id="cc-kw" label="Charger per van">
                <select id="cc-kw" value={inputs.chargerKw} onChange={set("chargerKw")} className={fieldInput}>
                  {CHARGER_OPTIONS.map((o) => (
                    <option key={o.kw} value={String(o.kw)}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <p className="font-mono text-[12px] text-muted">
                About {fmt(p.energy, 1)} kWh per van per night, {fmt(p.win.length * SLOT_HOURS, 1)} h at the depot.
                Connection {fmt(p.cap)} kW.
              </p>
            </fieldset>

            <details>
              <summary className="cursor-pointer text-[13px] text-muted">Tariff and assumptions</summary>
              <fieldset className="mt-2.5 grid gap-2.5">
                <div className="grid grid-cols-2 gap-2.5">
                  <Field id="cc-dayp" label="Day rate" unit="p/kWh">
                    <input id="cc-dayp" type="number" min={0} step={0.1} inputMode="decimal" value={inputs.dayRatePence} onChange={set("dayRatePence")} className={fieldInput} />
                  </Field>
                  <Field id="cc-nightp" label="Night rate" hint="00:00 to 07:00" unit="p/kWh">
                    <input id="cc-nightp" type="number" min={0} step={0.1} inputMode="decimal" value={inputs.nightRatePence} onChange={set("nightRatePence")} className={fieldInput} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <Field id="cc-days" label="Operating days" unit="/yr">
                    <input id="cc-days" type="number" min={1} max={366} step={1} inputMode="numeric" value={inputs.operatingDays} onChange={set("operatingDays")} className={fieldInput} />
                  </Field>
                  <Field id="cc-pf" label="Power factor">
                    <input id="cc-pf" type="number" min={0.5} max={1} step={0.01} inputMode="decimal" value={inputs.powerFactor} onChange={set("powerFactor")} className={fieldInput} />
                  </Field>
                </div>
              </fieldset>
            </details>
          </form>

          <div className="grid min-w-0 gap-4" aria-live="polite">
            <section className="grid gap-4 rounded-xl border border-border bg-surface p-5 shadow-card">
              <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-border">
                <div className="grid gap-0.5 px-4 py-3.5">
                  <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted">Charge on arrival</span>
                  <span className="font-display text-[clamp(40px,7vw,64px)] font-bold leading-none tabular-nums">{fmt(r.maxOnArrival)}</span>
                  <span className="text-[13px] text-muted">vans fit</span>
                </div>
                <div className="grid gap-0.5 border-l border-border bg-accent/10 px-4 py-3.5">
                  <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted">Smart charging</span>
                  <span className="font-display text-[clamp(40px,7vw,64px)] font-bold leading-none text-accent tabular-nums">{fmt(r.maxSmart)}</span>
                  <span className="text-[13px] text-muted">vans fit</span>
                </div>
              </div>
              <p className="m-0 font-display text-[20px] font-semibold leading-snug [text-wrap:balance]">
                {head.warn ? <span className="text-red-400">{head.warn} </span> : null}
                {head.rest}
              </p>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-2.5">
                {facts.map((f) => (
                  <div key={f.k} className={`grid gap-0.5 border-t-2 pt-2 ${toneBorder[f.tone]}`}>
                    <span className="text-[12px] text-muted">{f.k}</span>
                    <span className={`font-mono text-[18px] font-medium tabular-nums ${toneText[f.tone]}`}>{f.v}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid min-w-0 gap-2.5 rounded-xl border border-border bg-surface px-4 pb-3 pt-4 shadow-card">
              <h2 className="m-0 font-display text-[18px] font-bold">Your depot&apos;s night with {p.n} vans</h2>
              <div className="flex flex-wrap gap-3.5 text-[12px] text-muted">
                <Legend className="stroke-amber-400" label="Connection limit" dashed />
                <Legend className="stroke-red-400" label="Charge on arrival" />
                <Legend className="stroke-accent" label="Smart charging" />
                <Legend className="stroke-slate-600" label="Site load without vans" thick />
              </div>
              <NightChart r={r} cursor={cursor} />
              <div className="grid gap-1.5">
                <label htmlFor="cc-scrub" className="text-[12px] text-muted">
                  Move through the night
                </label>
                <input
                  id="cc-scrub"
                  type="range"
                  min={0}
                  max={SLOTS - 1}
                  step={1}
                  value={cursor}
                  onChange={(e) => setCursor(Number(e.target.value))}
                  className="w-full accent-emerald-600"
                />
                <p className="m-0 min-h-[2.6em] font-mono text-[13px] text-foreground">
                  {slotLabel(s)} to {slotLabel(s + 1)}: smart {fmt(r.smartSite[s])} kW of {fmt(p.cap)} kW, {fmt(spare)} kW spare
                  {inWindow ? `, each van at ${fmt(perVan, 1)} kW.` : ", vans out."}
                  <br />
                  Charged on arrival: {fmt(arrivalAtCursor)} kW
                  {arrivalOver ? <span className="font-medium text-red-400"> ({fmt(arrivalAtCursor - p.cap)} kW over)</span> : null}.
                </p>
              </div>
            </section>

            <section className="grid gap-3 rounded-xl bg-slate-100 p-5 text-slate-950">
              <h2 className="m-0 font-display text-[18px] font-bold">Want the real answer for your site?</h2>
              <p className="m-0 max-w-[64ch] opacity-80">
                This check uses one flat load figure and one shift pattern. A DepotTwin study uses your half-hourly meter
                data, each group of vehicles&apos; shift times and daily energy, and your actual tariff, then adds:
              </p>
              <ul className="m-0 grid list-disc gap-1 pl-5 opacity-90">
                <li>battery and solar sizing, with the extra vans each one makes room for</li>
                <li>a timed connection profile you can take to your DNO</li>
                <li>the charging cost on your tariff, and a five-year view</li>
                <li>
                  from your charge point session export, what your charging cost and what the same sessions would have
                  cost in the cheapest half hours
                </li>
              </ul>
              <p className="m-0 max-w-[64ch] text-[13px] opacity-80">
                Every figure in a study is projected from the data you send, and the report states its method and assumptions.
              </p>
              <div className="flex flex-wrap items-center gap-2.5">
                <a
                  href="#cc-request"
                  className="inline-flex items-center rounded-md bg-accent px-4 py-2.5 text-[14px] font-semibold text-accent-foreground transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  Request a DepotTwin study
                </a>
                <button
                  type="button"
                  onClick={onCopy}
                  className="inline-flex items-center rounded-md bg-slate-950 px-4 py-2.5 text-[14px] font-semibold text-slate-100 transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  Copy results
                </button>
                <span className="text-[14px]">
                  or email <span className="select-all font-mono">{CONTACT_EMAIL}</span>
                </span>
              </div>
              {copyState === "copied" ? (
                <p className="m-0 text-[12px]">
                  Copied. Paste it into an email to {CONTACT_EMAIL} and we will reply with what a full study would show.
                </p>
              ) : null}
              {copyState === "blocked" ? (
                <div className="grid gap-1.5">
                  <p className="m-0 text-[12px]">
                    Copy is blocked here. Select the text below and include it in an email to {CONTACT_EMAIL}.
                  </p>
                  <textarea
                    readOnly
                    value={summary}
                    rows={7}
                    aria-label="Your results"
                    onFocus={(e) => e.currentTarget.select()}
                    className="w-full rounded-md border border-slate-700 bg-slate-950 p-2 font-mono text-[12px] text-slate-100"
                  />
                </div>
              ) : null}
            </section>

            <CapacityCheckRequest inputs={inputs} result={r} mailto={mailto} />

            <p className="m-0 max-w-[80ch] text-[12px] text-muted">
              {DISCLAIMER} Vans are assumed identical and back at the same time; windows use whole half hours inside the
              dwell; smart charging never takes the site over its connection, and a van it cannot fill is counted as not
              fitting. Charging losses of 10% are added to each van&apos;s energy. Nothing on this page is guaranteed.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
